import { c as _c } from "react/compiler-runtime";
import { LoadingOutlined } from '@ant-design/icons';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';
import { NotificationPlacements } from 'antd/es/notification/interface';
import { get } from 'es-toolkit/compat';
import { useAtomValue } from 'jotai';
import { useSession } from 'next-auth/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { BillingQuoteRequestFlowDict, SubscriptionStatus } from '@/api/virtual-lab-svc/queries/types';
import { useAppNotification } from '@/components/notification';
import { BillingSummary } from '@/features/payments/billing-summary';
import { useBillingQuoteQuery } from '@/features/payments/hooks';
import PricingToggleCards from '@/features/payments/subscription/pricing-toggle-cards';
import { DefaultCurrency, FlowStepDict, flowAtom } from '@/features/payments/subscription/shared';
import { getBackendPaymentErrorDescription, getStripeSetupErrorDescription, isSetupIntentConsumedError } from '@/features/stripe/errors';
import { resolvePaymentMethodId, useSetupIntentQuery, useStripeInstanceQuery } from '@/features/stripe/hooks';
import { BillingAddressElement, BillingCardElement, buildStripeFormOptions } from '@/features/stripe/payment-elements';
import { messages } from '@/i18n/en/payment';
import { useWorkspace } from '@/ui/hooks/use-workspace';
import { Button } from '@/ui/molecules/button';
import { keyBuilder } from '@/ui/use-query-keys/user';
import { cn } from '@/utils/css-class';
import { useCreateSubscriptionMutation } from './hooks';
import type { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import type { User } from 'next-auth';
import type { CreditConversionResponse, TBillingAddress } from '@/api/virtual-lab-svc/queries/types';
type Props = {
  onPrevious: () => void;
  onSetupIntentRefreshNeeded: () => Promise<void>;
};
const notificationConfig = {
  placement: NotificationPlacements[2],
  key: 'subscription-payment-error'
};
function Form({
  onPrevious,
  onSetupIntentRefreshNeeded
}: Props) {
  const {
    virtualLabId
  } = useWorkspace();
  const queryClient = useQueryClient();
  const elements = useElements();
  const stripe = useStripe();
  const {
    data: session
  } = useSession();
  const {
    interval,
    tier
  } = useAtomValue(flowAtom);
  const [billingAddress, setBillingAddress] = useState<TBillingAddress | null>(null);
  const [stripeElementsReady, setElementsReady] = useState(false);
  const [isSubscribing, setSubscribing] = useState(false);
  const [saveBillingAddressToProfile, setSaveBillingAddressToProfile] = useState<boolean>(false);
  const [cachedPaymentMethodId, setCachedPaymentMethodId] = useState<string | null>(null);
  const cachedPaymentMethodIdRef = useRef<string | null>(null);
  const {
    success: successNotify,
    error: errorNotify
  } = useAppNotification();
  const createSubscription = useCreateSubscriptionMutation();
  const quotePayload = useMemo(() => billingAddress && tier?.app_id && virtualLabId ? {
    interval,
    virtual_lab_id: virtualLabId,
    flow: BillingQuoteRequestFlowDict.Subscription,
    tier_id: tier.app_id,
    currency: DefaultCurrency,
    billing_address: billingAddress
  } : null, [billingAddress, interval, tier, virtualLabId]);
  const quote = useBillingQuoteQuery({
    payload: quotePayload,
    enabled: Boolean(quotePayload)
  });
  const selectedPrice = useMemo(() => tier?.prices.find(price => price.interval === interval), [interval, tier?.prices]);
  const billingSummaryFallback: CreditConversionResponse | null = selectedPrice ? {
    credits: 0,
    currency: selectedPrice.currency,
    amount: selectedPrice.discount || selectedPrice.amount,
    rate: BillingQuoteRequestFlowDict.Subscription
  } : null;
  const formLoaded = stripe && elements;
  const disableForm = !formLoaded || !stripeElementsReady || isSubscribing || quote.isFetching || !quote.data;
  const user = session?.user as User;
  const currentBillingAddress = billingAddress?.country ? billingAddress : null;
  const disablePaying = !stripe || !stripeElementsReady || !elements || !quote.data || !currentBillingAddress || !user || !tier?.app_id || quote.isFetching || isSubscribing;
  const cachePaymentMethodId = useCallback((paymentMethodId: string) => {
    cachedPaymentMethodIdRef.current = paymentMethodId;
    setCachedPaymentMethodId(paymentMethodId);
  }, []);
  const clearCachedPaymentMethodId = useCallback(() => {
    cachedPaymentMethodIdRef.current = null;
    setCachedPaymentMethodId(null);
  }, []);
  const handleCardChange = useCallback((event: StripePaymentElementChangeEvent) => {
    if (isSubscribing || !cachedPaymentMethodIdRef.current || event.empty) {
      return;
    }
    clearCachedPaymentMethodId();
    void onSetupIntentRefreshNeeded();
  }, [clearCachedPaymentMethodId, isSubscribing, onSetupIntentRefreshNeeded]);
  const onSubmit = async (event_0: React.SubmitEvent<HTMLFormElement>) => {
    event_0.preventDefault();
    event_0.stopPropagation();
    if (disablePaying) {
      return;
    }
    setSubscribing(true);
    let paymentMethodId_0: string;
    try {
      paymentMethodId_0 = await resolvePaymentMethodId({
        billingAddress: currentBillingAddress,
        cachedPaymentMethodId,
        elements,
        returnUrl: window.location.href,
        stripe,
        user
      });
      cachePaymentMethodId(paymentMethodId_0);
    } catch (error) {
      if (isSetupIntentConsumedError(error)) {
        clearCachedPaymentMethodId();
        await onSetupIntentRefreshNeeded();
      }
      errorNotify({
        message: messages.paymentProcessingErrorTitle,
        description: getStripeSetupErrorDescription(error),
        ...notificationConfig
      });
      setSubscribing(false);
      return;
    }
    try {
      const {
        data
      } = await createSubscription.mutateAsync({
        interval,
        tier_id: tier.app_id,
        quote_id: quote.data.quote_id,
        billing_address: currentBillingAddress,
        sync_billing_address_to_profile: saveBillingAddressToProfile,
        payment_method_id: paymentMethodId_0
      });
      if (saveBillingAddressToProfile) {
        void queryClient.invalidateQueries({
          queryKey: keyBuilder.profile()
        });
      }
      if (data?.subscription.status === SubscriptionStatus.ACTIVE) {
        successNotify({
          message: messages.subscriptionPaymentSuccess,
          description: messages.subscriptionPaymentSuccessDescription,
          ...notificationConfig
        });
      }
      clearCachedPaymentMethodId();
      setSubscribing(false);
    } catch (error_0) {
      const code = get(error_0, 'cause.code', 'DEFAULT');
      errorNotify({
        message: messages.paymentProcessingErrorTitle,
        description: getBackendPaymentErrorDescription(code, 'subscription'),
        ...notificationConfig
      });
      setSubscribing(false);
    }
  };
  return <form data-testid="subscription-payment-from" name="stripe-payment-flow-step" className="mx-auto flex h-full min-h-0 w-full flex-col overflow-y-auto secondary-scrollbar px-4 py-6" onSubmit={onSubmit}>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
        <PricingToggleCards />
        <BillingSummary quote={quote.data ?? null} conversion={billingSummaryFallback} loading={quote.isFetching} />
        <div className="rounded-2xl border border-gray-200 bg-white/5 p-4 text-primary-9">
          <div className="mb-4 font-semibold">Payment method</div>
          <div className="flex flex-col gap-4">
            <BillingAddressElement address={billingAddress} disabled={isSubscribing} framed={false} onAddressChange={setBillingAddress} onSaveAddressChange={checked => setSaveBillingAddressToProfile(Boolean(checked))} saveAddress={saveBillingAddressToProfile} />
            <BillingCardElement disabled={isSubscribing} framed={false} onChange={handleCardChange} onReady={() => setElementsReady(true)} paymentElementId="subscription-form" />
          </div>
          <div className="mt-5 flex items-center justify-end gap-3">
            <Button rounded type="button" variant="ghost" size="lg" className={cn('w-max border border-none text-primary-9', 'shadow-[inset_0_0_0_1px_#fff,0_0_0_1px_rgba(0,0,0,0.04)]', 'hover:bg-gray-100')} disabled={isSubscribing} onClick={onPrevious}>
              Cancel
            </Button>
            <Button rounded type="submit" variant="default" size="lg" className={cn('w-max border border-none shadow-2xl text-white', 'hover:bg-primary-8')} disabled={disableForm}>
              <div className="flex items-center gap-2 px-6">
                Pay
                {isSubscribing && <LoadingOutlined spin />}
              </div>
            </Button>
          </div>
        </div>
      </div>
    </form>;
}
export default function PaymentForm(t0) {
  const $ = _c(17);
  const {
    onPrevious
  } = t0;
  const {
    step
  } = useAtomValue(flowAtom);
  const [elementsKey, setElementsKey] = useState(0);
  const t1 = step === FlowStepDict.Pay;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1,
      virtualLabId: "subscription"
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: setupIntentData,
    isLoading: isSetupIntentLoading,
    refetch: refetchSetupIntent
  } = useSetupIntentQuery(t2);
  const t3 = step === FlowStepDict.Pay;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      enabled: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const stripe = useStripeInstanceQuery(t4);
  let t5;
  if ($[4] !== refetchSetupIntent) {
    t5 = async () => {
      await refetchSetupIntent();
      setElementsKey(_temp);
    };
    $[4] = refetchSetupIntent;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const refreshSetupIntent = t5;
  if (isSetupIntentLoading || stripe.isLoading) {
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex h-full grow items-center justify-center py-7"><Spin size="large" indicator={<LoadingOutlined className="text-white" />} /></div>;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    return t6;
  }
  if (!setupIntentData?.data?.client_secret || !stripe.data) {
    return null;
  }
  const t6 = stripe.data;
  let t7;
  if ($[7] !== setupIntentData.data.client_secret) {
    t7 = buildStripeFormOptions(setupIntentData.data.client_secret);
    $[7] = setupIntentData.data.client_secret;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== onPrevious || $[10] !== refreshSetupIntent) {
    t8 = <Form onPrevious={onPrevious} onSetupIntentRefreshNeeded={refreshSetupIntent} />;
    $[9] = onPrevious;
    $[10] = refreshSetupIntent;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== elementsKey || $[13] !== stripe.data || $[14] !== t7 || $[15] !== t8) {
    t9 = <div className="flex h-full min-h-0 grow flex-col"><Elements key={elementsKey} stripe={t6} options={t7}>{t8}</Elements></div>;
    $[12] = elementsKey;
    $[13] = stripe.data;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
function _temp(currentKey) {
  return currentKey + 1;
}